<template>
  <div class="questions-panel-wrapper">
    <section class="questions-panel">
      <div
          v-if="questionStore.showComprehensiveEscalationNotice"
          class="questions-panel__comprehensive-notice"
          role="status"
      >
        {{ $t('app.assessment_mode.text.input.comprehensive_escalation_notice') }}
      </div>

      <h2 class="questions-panel__title">
        {{ $t(questionsHeaderToken) }}
      </h2>

      <div class="questions-panel__list">
        <QuestionCard
            v-for="(question, idx) in questions"
            :key="question.id"
            :question="question"
            :index="idx"
            :is-unanswered="validationAttempted && !isQuestionAnswered(question)"
            @toggle="handleToggle"
            @answer="handleAnswer"
        />
      </div>

      <div class="questions-panel__footer">
        <button
            type="button"
            class="questions-panel__submit-btn"
            :disabled="submitted || isSubmitting"
            @click="handleSubmit"
        >
          {{ $t('app.assessment_mode.text.input.questions_submit') }}
        </button>
      </div>
    </section>

    <transition name="questions-success-fade">
      <div v-if="submitted" class="questions-panel__success">
        <span class="questions-panel__success-icon">✓</span>
        {{ $t('app.assessment_mode.text.input.questions_complete') }}
      </div>
    </transition>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType, ref, watch } from 'vue';
import QuestionCard from './QuestionCard.vue';
import type { QuestionItem, QuestionStage } from '../../types/text-mode.types';
import { useQuestionStore } from '../../stores/question.store';

export default defineComponent({
  name: 'QuestionsPanel',

  components: {
    QuestionCard,
  },

  props: {
    questions: {
      type: Array as PropType<QuestionItem[]>,
      required: true,
    },
    stage: {
      type: String as PropType<QuestionStage>,
      default: undefined,
    },
    hasNetworkError: {
      type: Boolean,
      default: false,
    },
    isSubmitting: {
      type: Boolean,
      default: false,
    },
  },

  setup(props, { emit }) {
    const questionStore = useQuestionStore();
    const submitted = ref(false);
    const validationAttempted = ref(false);

    const questionsHeaderToken = computed(() =>
      props.stage === 'confirm'
        ? 'app.assessment_mode.text.input.questions_header.confirm'
        : 'app.assessment_mode.text.input.questions_header'
    );

    const isQuestionAnswered = (question: QuestionItem): boolean => {
      const value = String(question.answer ?? '').trim();
      return value.length > 0;
    };

    const allQuestionsAnswered = computed<boolean>(() => {
      return props.questions.length > 0 && props.questions.every(isQuestionAnswered);
    });

    const handleToggle = (questionId: string): void => {
      emit('toggle', questionId);
    };

    const handleAnswer = (payload: { questionId: string; answer: string }): void => {
      emit('answer', payload);
    };

    const handleSubmit = (): void => {
      if (submitted.value || props.isSubmitting) return;
      if (!allQuestionsAnswered.value) {
        validationAttempted.value = true;
        return;
      }
      validationAttempted.value = false;
      if (!questionStore.prepareQuestionsForSubmit()) return;
      submitted.value = true;
      emit('submit', props.questions);
    };

    watch(
      () => props.questions,
      () => { submitted.value = false; },
    );

    watch(
      () => props.hasNetworkError,
      (hasNetworkError) => {
        if (hasNetworkError) submitted.value = false;
      },
    );

    watch(
      () => props.questions.map(question => question.id).join('|'),
      () => { validationAttempted.value = false; },
    );

    return {
      questionStore,
      submitted,
      validationAttempted,
      allQuestionsAnswered,
      isQuestionAnswered,
      handleToggle,
      handleAnswer,
      handleSubmit,
      questionsHeaderToken,
    };
  },
});
</script>
